import { Bell, MessageSquare, ListTodo, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useRightSidebar } from "@/contexts/RightSidebarContext";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationsPanel } from "./sidebar/NotificationsPanel";
import {
  CommentsPanel,
  useUnresolvedCommentCount,
} from "./sidebar/CommentsPanel";
import { MyTasksPanel } from "./sidebar/MyTasksPanel";
import { cn } from "@/lib/utils";
import type { SidebarTab } from "@/contexts/RightSidebarContext";

function TabBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="ml-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function RightSidebar() {
  const { isOpen, activeTab, openToTab, closeSidebar, currentEntity } =
    useRightSidebar();
  const { unreadCount } = useNotifications();
  const unresolvedCommentCount = useUnresolvedCommentCount();

  return (
    <div
      className={cn(
        "border-l bg-background transition-all duration-200 ease-in-out overflow-hidden shrink-0",
        isOpen ? "w-[22rem]" : "w-0",
      )}
    >
      <div className="w-[22rem] h-full flex flex-col overscroll-contain">
        <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
          <Tabs
            value={activeTab}
            onValueChange={(v) => openToTab(v as SidebarTab)}
            className="w-full"
          >
            <div className="flex items-center justify-between">
              <TabsList className="h-8">
                <TabsTrigger value="tasks" className="text-xs px-2 h-7">
                  <ListTodo className="h-3.5 w-3.5 mr-1" />
                  Tasks
                </TabsTrigger>
                <TabsTrigger value="notifications" className="text-xs px-2 h-7">
                  <Bell className="h-3.5 w-3.5 mr-1" />
                  <span>Notif.</span>
                  <TabBadge count={unreadCount} />
                </TabsTrigger>
                {currentEntity && (
                  <TabsTrigger value="comments" className="text-xs px-2 h-7">
                    <MessageSquare className="h-3.5 w-3.5 mr-1" />
                    <span>Comments</span>
                    <TabBadge count={unresolvedCommentCount} />
                  </TabsTrigger>
                )}
              </TabsList>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={closeSidebar}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Tabs>
        </div>

        <div className="flex-1 overflow-hidden">
          {activeTab === "tasks" && <MyTasksPanel />}
          {activeTab === "notifications" && <NotificationsPanel />}
          {activeTab === "comments" && <CommentsPanel />}
        </div>
      </div>
    </div>
  );
}
